'use client';

/**
 * useOperationsDashboardRealtime
 *
 * Subscribes to Supabase realtime changes on:
 *   - duty_assignments  (unassigned duties, fleet utilization, duty status)
 *   - crew_members      (crew availability)
 *
 * Computes live KPI values and re-fetches whenever data changes.
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

export interface DashboardKPIs {
  unassignedDuties: number;
  totalTodayDuties: number;
  fleetUtilized: number;
  fleetTotal: number;
  crewAvailable: number;
  crewTotal: number;
  dutiesCompleted: number;
  dutiesActive: number;
  dutiesScheduled: number;
  lastRefreshed: Date | null;
}

interface UseOperationsDashboardRealtimeReturn {
  kpis: DashboardKPIs;
  isConnected: boolean;
  isLoading: boolean;
  lastEvent: { table: string; eventType: string; timestamp: Date } | null;
}

const DEFAULT_KPIS: DashboardKPIs = {
  unassignedDuties: 0,
  totalTodayDuties: 0,
  fleetUtilized: 0,
  fleetTotal: 0,
  crewAvailable: 0,
  crewTotal: 0,
  dutiesCompleted: 0,
  dutiesActive: 0,
  dutiesScheduled: 0,
  lastRefreshed: null,
};

export function useOperationsDashboardRealtime(): UseOperationsDashboardRealtimeReturn {
  const [kpis, setKpis] = useState<DashboardKPIs>(DEFAULT_KPIS);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [lastEvent, setLastEvent] = useState<{ table: string; eventType: string; timestamp: Date } | null>(null);

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMounted = useRef(true);

  const fetchKPIs = useCallback(async (silent = false) => {
    if (!silent) setIsLoading(true);
    try {
      const supabase = createClient();
      const today = new Date().toISOString().split('T')[0];

      // Fetch today's duties and crew in parallel
      const [dutiesResult, crewResult] = await Promise.all([
        supabase
          .from('duty_assignments')
          .select('id, status, driver1_name, conductor_name, bus_number')
          .eq('duty_date', today),
        supabase
          .from('crew_members')
          .select('id, status, role'),
      ]);

      if (!isMounted.current) return;

      const duties = dutiesResult.data ?? [];
      const crew = crewResult.data ?? [];

      // Unassigned: duties where driver or conductor is missing/placeholder
      const unassigned = duties.filter(
        (d) => !d.driver1_name || d.driver1_name === '—' || !d.conductor_name || d.conductor_name === '—'
      ).length;

      // Fleet utilization: duties with a bus_number that are Active or Scheduled
      const activeDuties = duties.filter((d) => d.status === 'Active');
      const scheduledDuties = duties.filter((d) => d.status === 'Scheduled');
      const completedDuties = duties.filter((d) => d.status === 'Completed');

      // Unique buses in use today (Active + Scheduled)
      const busesInUse = new Set(
        duties
          .filter((d) => d.status === 'Active' || d.status === 'Scheduled')
          .map((d) => d.bus_number)
          .filter(Boolean)
      ).size;
      const totalBuses = new Set(duties.map((d) => d.bus_number).filter(Boolean)).size;

      // Crew availability
      const availableCrew = crew.filter((c) => c.status === 'Available').length;

      if (!isMounted.current) return;

      setKpis({
        unassignedDuties: unassigned,
        totalTodayDuties: duties.length,
        fleetUtilized: busesInUse,
        fleetTotal: Math.max(totalBuses, busesInUse),
        crewAvailable: availableCrew,
        crewTotal: crew.length,
        dutiesCompleted: completedDuties.length,
        dutiesActive: activeDuties.length,
        dutiesScheduled: scheduledDuties.length,
        lastRefreshed: new Date(),
      });
    } catch (err) {
      console.error('useOperationsDashboardRealtime: fetchKPIs error', err);
    } finally {
      if (isMounted.current && !silent) setIsLoading(false);
    }
  }, []);

  const scheduleRefresh = useCallback(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      fetchKPIs(true);
    }, 800); // increased from 500ms → 800ms to better coalesce rapid events
  }, [fetchKPIs]);

  useEffect(() => {
    isMounted.current = true;
    fetchKPIs(false);

    const supabase = createClient();

    const dutyChannel = supabase
      .channel('ops_dashboard_duties')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'duty_assignments' },
        (payload) => {
          if (!isMounted.current) return;
          setLastEvent({ table: 'duty_assignments', eventType: payload.eventType, timestamp: new Date() });
          scheduleRefresh();
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED' && isMounted.current) setIsConnected(true);
      });

    const crewChannel = supabase
      .channel('ops_dashboard_crew')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'crew_members' },
        (payload) => {
          if (!isMounted.current) return;
          setLastEvent({ table: 'crew_members', eventType: payload.eventType, timestamp: new Date() });
          scheduleRefresh();
        }
      )
      .subscribe();

    return () => {
      isMounted.current = false;
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      supabase.removeChannel(dutyChannel);
      supabase.removeChannel(crewChannel);
      setIsConnected(false);
    };
  }, [fetchKPIs, scheduleRefresh]);

  return { kpis, isConnected, isLoading, lastEvent };
}
